import { GridActionsCellItem, GridCellModesModel, GridCellParams, GridColDef, GridRowId, GridRowModel, GridRowsProp, GridSortDirection, GridSortModel, GridToolbar, GridTreeNode, GridValidRowModel } from "@mui/x-data-grid";
import React, { FC, MutableRefObject, useState, useCallback, useMemo, useEffect, useRef, memo } from "react";
import { ReactElement } from "react";
import { ReactComponent as SaveIcon } from "../icons/save_item.svg";
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { handleCellClickAsEditing, handleCellModesModelChange } from "../util/tableHandlers";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import GlobalStyles from "@mui/material/GlobalStyles";
import { useTranslation } from "react-i18next";
import { StyledDataGrid } from "./styledGrid";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { TooltipIcon } from "./tooltipIcon";
import ConfirmationPanel from "./confirmationPanel";
import { deDE as deDEGrid, enUS as enUSGrid } from '@mui/x-data-grid/locales';
import LogoLoader from "./LogoLoader";

/** Minimum ms the loading overlay stays visible once shown. */
const LOADER_MIN_DISPLAY_MS = 1700;

const GridLoadingOverlay: FC = () => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <LogoLoader size={52} label={t('general.loading')} />
        </Box>
    );
};

/** Renders as a Divider inside the DataGrid actions menu. */
const MenuSeparator: FC<{ showInMenu?: boolean }> = () => <Divider />;

/** Global style: make all DataGrid action menus white. */
const gridMenuStyles = <GlobalStyles styles={{ '.MuiDataGrid-menuList': { backgroundColor: '#ffffff' } }} />;

interface EditableGridProps<R extends GridValidRowModel = any> {
    apiRef?: MutableRefObject<GridApiCommunity>;
    toolbar?: React.JSXElementConstructor<any>;
    onDelete?: (item: R) => void;
    disableDeleteModal?: boolean;
    modelType?: string;
    toolbarProps?: any;
    dataGridSlotProps?: any;
    processRowUpdate?: (newRow: R, oldRow: R) => Promise<R> | R;
    isCellEditable?: (params: GridCellParams<any, GridValidRowModel, GridValidRowModel, GridTreeNode>) => boolean;
    extraActions?: (id: GridRowId, row?: R) => JSX.Element[];
    saveAction?: boolean;
    actionsHeaderKey?: string;
    actionsColumnWidth?: number;
    rows: R[];
    columns: readonly GridColDef<R>[];
    height: string;
    sortField?: string;
    sortDirection?: GridSortDirection;
    updateSortModel?: (newSortModel: GridSortModel) => void;
    onCellDoubleClick?: (params: GridCellParams<any, GridValidRowModel, GridValidRowModel, GridTreeNode>) => void;
    isDeleteDisabled?: (row: R) => boolean;
    highlightedRowId?: GridRowId;
    sortingMode?: 'client' | 'server';
    loading?: boolean;
    deleteInMenu?: boolean;
}

const EditableGrid: FC<EditableGridProps> = memo((props: EditableGridProps): ReactElement => {
    const [changedRow, setChangedRow] = React.useState<GridRowId>(undefined);
    const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});
    const { t, i18n } = useTranslation();
    // Track local optimistic-delete removals as a Set of ids so we don't fight
    // parent-driven row updates. Previously we mirrored props.rows into local
    // state via useEffect, which produced a one-frame "old rows -> new rows"
    // flash on every parent re-sort/filter (visible double-sort race in v9).
    const [locallyDeletedIds, setLocallyDeletedIds] = useState<Set<GridRowId>>(() => new Set());

    // Debounced loading: when the parent turns loading off, keep the overlay
    // visible for at least LOADER_MIN_DISPLAY_MS so users always see it.
    const [deferredLoading, setDeferredLoading] = useState(props.loading ?? false);
    const loaderStartRef = useRef<number | null>(props.loading ? Date.now() : null);
    const loaderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (props.loading) {
            if (loaderTimerRef.current) {
                clearTimeout(loaderTimerRef.current);
                loaderTimerRef.current = null;
            }
            loaderStartRef.current = Date.now();
            setDeferredLoading(true);
        } else {
            const elapsed = loaderStartRef.current != null ? Date.now() - loaderStartRef.current : LOADER_MIN_DISPLAY_MS;
            const remaining = LOADER_MIN_DISPLAY_MS - elapsed;
            if (remaining > 0) {
                loaderTimerRef.current = setTimeout(() => setDeferredLoading(false), remaining);
            } else {
                setDeferredLoading(false);
            }
        }
        return () => {
            if (loaderTimerRef.current) {
                clearTimeout(loaderTimerRef.current);
            }
        };
    }, [props.loading]);
    const isGerman = i18n.language?.toLowerCase().startsWith('de');
    const gridLocaleText = React.useMemo(() => ({
        ...((isGerman ? deDEGrid : enUSGrid).components.MuiDataGrid.defaultProps.localeText),
        toolbarColumns: t('toolbar.columns'),
        toolbarFilters: t('toolbar.filters'),
        toolbarDensity: t('toolbar.density'),
        toolbarExport: t('toolbar.export.exportButton'),
        toolbarColumnsLabel: t('toolbar.columnsLabel'),
        toolbarFiltersLabel: t('toolbar.filtersLabel'),
        toolbarDensityLabel: t('toolbar.densityLabel'),
        toolbarExportLabel: t('toolbar.export.exportLabel'),
    }), [isGerman, t]);

    // Reset the optimistic-delete set whenever the parent ships a new rows
    // reference (e.g. after a refetch); the parent is now authoritative.
    useEffect(() => {
        setLocallyDeletedIds(prev => (prev.size === 0 ? prev : new Set()));
    }, [props.rows]);

    const items = useMemo(
        () => locallyDeletedIds.size === 0
            ? props.rows
            : props.rows.filter(r => !locallyDeletedIds.has(r.id as GridRowId)),
        [props.rows, locallyDeletedIds],
    );

    const [deleteItem, setDeleteItem] = useState<GridRowModel<GridValidRowModel>>(null);
    const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);

    // Handle the displaying of the modal based on id - memoized
    const showDeleteModal = useCallback((id: GridRowId) => () => {
        const item = props.rows.find(r => r.id === id.toString());
        if (props.disableDeleteModal) {
            props.onDelete(item);
            setLocallyDeletedIds(prev => {
                const next = new Set(prev);
                next.add(item.id as GridRowId);
                return next;
            });
            return;
        }
        setDeleteItem(item);
        setDeleteConfirmationModal(true);
    }, [props.rows, props.disableDeleteModal, props.onDelete]);

    // Hide the modal
    const hideConfirmationModal = useCallback(() => {
        setDeleteConfirmationModal(false);
    }, []);

    const handleDeleteClick = useCallback((item: GridRowModel<GridValidRowModel>) => {
        props.onDelete(item);
        setLocallyDeletedIds(prev => {
            const next = new Set(prev);
            next.add(item.id as GridRowId);
            return next;
        });
    }, [props.onDelete]);

    // Handle the actual deletion of the item
    const submitDelete = () => {
        handleDeleteClick(deleteItem);
        setDeleteConfirmationModal(false);
    };

    //default sorting
    const [sortModel, setSortModel] = React.useState<GridSortModel>(
        props.sortField ? [{
            field: props.sortField,
            sort: props.sortDirection || 'asc'
        }] : []
    );

    const updateSortModel = useCallback((newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
        if (props.updateSortModel) {
            props.updateSortModel(newSortModel);
        }
    }, [props.updateSortModel]);

    const processRowUpdate = (newRow: GridValidRowModel, oldRow: GridValidRowModel): Promise<GridValidRowModel> | GridValidRowModel => {
        setChangedRow(undefined);
        if (props.processRowUpdate) {
            return props.processRowUpdate(newRow, oldRow);
        }
    }

    const getActions = (id: GridRowId, row: GridValidRowModel) => {
        const actions = [];
        if (props.saveAction)
            actions.push(
                <GridActionsCellItem
                    key="save"
                    icon={<SaveIcon />}
                    label="Save"
                    style={{ color: "var(--mui-palette-primary-main)" }}
                    disabled={!id || id !== changedRow}
                />,
            );

        if (props.extraActions) {
            props.extraActions(id, row).forEach((element) => {
                actions.push(element);
            });
        }
        if (props.onDelete) {
            const isDisabled = props.isDeleteDisabled ? props.isDeleteDisabled(row as any) : false;
            if (props.deleteInMenu) {
                actions.push(<MenuSeparator key="delete-separator" showInMenu />);
            }
            actions.push(
                <GridActionsCellItem
                    key="delete"
                    icon={<TooltipIcon titleKey={'general.delete'} icon={<DeleteIcon />} />}
                    label="Delete"
                    onClick={showDeleteModal(id)}
                    disabled={isDisabled}
                    style={{ color: isDisabled ? "var(--mui-palette-action-disabled)" : "var(--mui-palette-primary-main)" }}
                    showInMenu={props.deleteInMenu ?? false}
                />
            );
        }
        return actions;
    }

    const actionsColumn: GridColDef<any> = {
        field: 'actions',
        type: 'actions',
        headerName: t('meeting.actions'),
        width: props.actionsColumnWidth ?? 230,
        cellClassName: 'actions',
        getActions: ({ id, row }) => getActions(id, row)
    }

    const translate = (col: GridColDef) => {
        if (col.field.startsWith("progress")) return col;
        // Only translate if headerName looks like a translation key (contains a dot) - getting rid of the browser warning from i18next
        if (col.headerName && col.headerName.includes('.')) {
            return { ...col, headerName: t(col.headerName) };
        }
        return col;
    };

    // Memoize the final columns array so MUI X v9 doesn't recompute its
    // internal column-state on every parent render (visible flicker).
    const hasActionsColumn = props.saveAction || !!props.onDelete || !!props.extraActions;
    const resolvedColumns = useMemo(() => {
        const base = hasActionsColumn
            ? [...props.columns, { ...actionsColumn, headerName: props.actionsHeaderKey ?? actionsColumn.headerName }]
            : [...props.columns];
        return base.map(translate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.columns, hasActionsColumn, props.actionsHeaderKey, t, changedRow]);

    const onCellModesModelChange = useCallback(
        handleCellModesModelChange(setCellModesModel),
        [],
    );
    const onCellClick = useCallback(
        handleCellClickAsEditing(setCellModesModel, setChangedRow),
        [],
    );

    return (
        <Box sx={{ backgroundColor: "background.default" }}>
            {gridMenuStyles}
            <div style={{ height: props.height, width: '100%' }}>
                <StyledDataGrid rows={deferredLoading ? [] : items}
                    apiRef={props.apiRef}
                    pageSizeOptions={[1000]}
                    columns={resolvedColumns}
                    cellModesModel={cellModesModel}
                    onCellModesModelChange={onCellModesModelChange}
                    onCellClick={onCellClick}
                    processRowUpdate={processRowUpdate}
                    disableRowSelectionOnClick
                    getRowClassName={(params) => {
                        const classes = ['grid-theme'];
                        if (props.highlightedRowId && params.id === props.highlightedRowId) {
                            classes.push('row-highlight-animation');
                        }
                        return classes.join(' ');
                    }}
                    isCellEditable={props.isCellEditable}
                    sortModel={sortModel}
                    sortingMode={props.sortingMode}
                    disableColumnMenu
                    onSortModelChange={updateSortModel}
                    slots={{
                        toolbar: props.toolbar || GridToolbar,
                        loadingOverlay: GridLoadingOverlay,
                    }}
                    slotProps={{
                        ...props.toolbarProps,
                        ...props.dataGridSlotProps,
                    }}
                    loading={deferredLoading}
                    showToolbar
                    localeText={gridLocaleText}
                    onCellDoubleClick={(params) => {
                        props.onCellDoubleClick?.(params);
                    }}
                    sx={{
                        '& .MuiDataGrid-columnHeader[data-field="risk"] .MuiDataGrid-columnHeaderTitle.css-t89xny-MuiDataGrid-columnHeaderTitle': {
                            display: 'none'
                        }
                    }}
                />
            </div>
            {deleteConfirmationModal &&
                <ConfirmationPanel
                    showModal={deleteConfirmationModal}
                    confirmModal={submitDelete}
                    hideModal={hideConfirmationModal}
                    entry={deleteItem}
                    type={props.modelType}
                    action="delete"
                />}
        </Box>
    );
});

export default EditableGrid;
